import type { ReactNode } from 'react'
import { StationLedger } from '../ui'

export interface AppRootProps {
  children: ReactNode
}

/**
 * The wrapper every top-level screen renders inside — `<StationLedger
 * measure>` plus the `sl-stack sl-app` div that carries the vertical rhythm
 * between a screen's top-level sections *and* the page's own column
 * padding, on one element.
 *
 * Extracted here because `AppShell` and `Onboarding` are the app's only two
 * top-level screens — `App` renders exactly one of them, gated on whether a
 * profile exists — and they would otherwise each carry an identical copy of
 * this markup and the comment explaining it.
 *
 * **Why one element carries both jobs**, restated from where this lived
 * before the extraction (`AppShell`'s own comment, prior to this task):
 * `StationLedger` and its components carry no margins of their own
 * (`base.css`'s comment on `.sl-stack` names this), so `.sl-stack` is the
 * vertical-rhythm primitive that puts air between a screen's sections —
 * without it they render welded together, an `EmptyState` flush against
 * whatever is above it. `.sl-layout` is the system's other spacing
 * primitive, but it is the calculator's two-column result/inputs layout;
 * neither top-level screen here has a "result" concept, so `.sl-stack`
 * alone is the fit. `sl-app` (`src/styles/app.css`) rides on the same
 * element, matching OTcalculator's `<main className="sl-stack sl-app">` —
 * it is the one element that is both the vertical stack and the page's own
 * column, so it is also where the column's padding and safe-area insets
 * belong: an inner wrapper would double up with `.sl-stack`'s own gap, and
 * an outer one would sit outside the `StationLedger` root and lose the
 * theme it needs to read `--paper` from.
 */
export function AppRoot({ children }: AppRootProps) {
  return (
    <StationLedger measure>
      <div className="sl-stack sl-app">{children}</div>
    </StationLedger>
  )
}
